import { get as _get } from 'lodash';
import { v4 as uuid } from 'uuid';

import { DRPShopResponse, ShopsInfo, Status } from '../model/shops.response';
import { FullAddress } from '../types/type';

export async function getRepairShops(
  fullAddress: FullAddress,
  distance: string,
  urlKey: string,
  sessionId: string,
  custKey: string
) {
  try {
    let URI = '';
    if (fullAddress.houseNumber && fullAddress.streetName) {
      URI = `${process.env['NX_DCV2_API_BASE_URL']}shops?state=${fullAddress.stateName}&addressLine1=${fullAddress.houseNumber}%20${fullAddress.streetName}&city=${fullAddress.cityName}&distance=${distance}`;
    } else if (fullAddress.zipCode) {
      URI = `${process.env['NX_DCV2_API_BASE_URL']}shops?zip=${fullAddress.zipCode}&distance=${distance}`;
    } else if (fullAddress.latitude && fullAddress.longitude) {
      URI = `${process.env['NX_DCV2_API_BASE_URL']}shops?longitude=${fullAddress.longitude}&latitude=${fullAddress.latitude}&distance=${distance}`;
    }
    const headers = new Headers();
    headers.append('urlid', urlKey);
    headers.append('afe-session-id', sessionId);
    headers.append('Authorization', `Bearer ${custKey}`);
    headers.append('afe-trace-id', uuid());

    const response = await fetch(URI, {
      method: 'GET',
      headers: headers,
    });
    const responseData = await response.json();
    const { statusCode } = responseData;
    const { error } = responseData;
    const { message } = responseData;
    if (
      _get(responseData, 'response.status.code', '') === 200 ||
      _get(responseData, 'response.status.code', '') === 404 // The back end respond with 404 when there are no shops found.
    ) {
      return responseData.response;
    } else {
      return { error, statusCode, message };
    }
  } catch (error) {
    console.log('getRepairShops:error', error);
    return error;
  }
}

export async function getRentalShops(
  fullAddress: FullAddress,
  distance: string,
  urlKey: string,
  sessionId: string,
  custKey: string
) {
  try {
    let URI = '';
    if (fullAddress.houseNumber && fullAddress.streetName) {
      URI = `${process.env['NX_DCV2_API_BASE_URL']}rentallocations?state=${fullAddress.stateName}&addressLine1=${fullAddress.houseNumber}%20${fullAddress.streetName}&city=${fullAddress.cityName}&distance=${distance}`;
    } else if (fullAddress.zipCode) {
      URI = `${process.env['NX_DCV2_API_BASE_URL']}rentallocations?zip=${fullAddress.zipCode}&distance=${distance}`;
    } else if (fullAddress.latitude && fullAddress.longitude) {
      URI = `${process.env['NX_DCV2_API_BASE_URL']}rentallocations?longitude=${fullAddress.longitude}&latitude=${fullAddress.latitude}&distance=${distance}`;
    }
    const headers = new Headers();
    headers.append('urlid', urlKey);
    headers.append('afe-session-id', sessionId);
    headers.append('Authorization', `Bearer ${custKey}`);
    headers.append('afe-trace-id', uuid());

    const response = await fetch(URI, {
      method: 'GET',
      headers: headers,
    });
    if (response.status >= 200 && response.status <= 299) {
      const responseData = await response.json();
      if (_get(responseData, 'response.status.code', -1) === 200 && _get(responseData, 'response.branchDetails')) {
        return mapRentalShops(_get(responseData, 'response.branchDetails'), _get(responseData, 'response.status'));
      } else {
        throw new Error("'getShops' failed");
      }
    } else {
      throw new Error("'getShops' failed");
    }
  } catch (error) {
    return null;
  }
}

function mapRentalShops(items: any[], status: Status): DRPShopResponse {
  const formattedResults: ShopsInfo[] = [];

  items.forEach((item) => {
    formattedResults.push({
      id: _get(item, 'rentalLocationDetails.locationId'),
      name: _get(item, 'rentalLocationDetails.name'),
      address: {
        addressLine1: _get(item, 'rentalLocationDetails.address.addressLine1'),
        addressLine2: '',
        city: _get(item, 'rentalLocationDetails.address.city'),
        state: _get(item, 'rentalLocationDetails.address.state'),
        zipCode: _get(item, 'rentalLocationDetails.address.zip'),
        coordinates: {
          latitude: _get(item, 'rentalLocationDetails.geocoordinates.latitude'),
          longitude: _get(item, 'rentalLocationDetails.geocoordinates.longitude'),
        },
      },
      contactMethodDetail: {
        primaryPhoneNumber: _get(item, 'rentalLocationDetails.telephone.number'),
        fax: '',
        emailAddresses: [],
        websiteAddresses: [],
      },
      aseCertified: '',
      hoursOfOperation: mapHoursOfOp(_get(item, 'hoursOfOperation')),
      tier: '',
      typeId: '',
      entityId: '',
      isActive: true,
      languagesSpoken: [],
      distanceInfo: {
        distance: _get(item, 'distanceAway'),
        units: 'MILES',
      },
    });
  });

  return { status: status, shops: formattedResults };
}

function mapHoursOfOp(hours: string | any[]): string {
  if (Array.isArray(hours)) {
    let formattedHours = '';
    hours.forEach((day) => {
      const concatedHours = { day: '', hours: '' };
      if (day.openHour && day.closeHour) {
        concatedHours.day = day.dayOfWeek.toString().substring(0, 3);
        concatedHours.hours =
          convertFromMilitary(_get(day, 'openHour')) + '-' + convertFromMilitary(_get(day, 'closeHour'));
        formattedHours += concatedHours.day + ' ' + concatedHours.hours + '\n';
      }
    });
    return formattedHours;
  } else {
    return hours
      .replace(/SUNDAY/gi, 'Sun')
      .replace(/MONDAY/gi, 'Mon')
      .replace(/TUESDAY/gi, 'Tue')
      .replace(/WEDNESDAY/gi, 'Wed')
      .replace(/THURSDAY/gi, 'Thu')
      .replace(/FRIDAY/gi, 'Fri')
      .replace(/SATURDAY/gi, 'Sat')
      .replace(/AM/gi, 'am')
      .replace(/PM/gi, 'pm');
  }
}

function convertFromMilitary(time: string) {
  const militaryHour = parseInt(time.substring(0, 2), 10);
  let amPm = 'PM';
  if (militaryHour % 12 === militaryHour) {
    amPm = 'AM';
  }

  let hour = (parseInt(time.substring(0, 2), 10) % 12).toString();
  if (hour === '0') {
    hour = '12';
  }
  const min = time.substring(2, 4);
  return hour + ':' + min + amPm;
}
